import type { LoaderFunctionArgs } from '~/types/route-args';
import type { SerializeFrom } from '~/types/serialize-from';
import RootPage from '@modules/root/page/RootPage';
import { type ShouldRevalidateFunction } from 'react-router';
import { getThemeMetaTags } from '@modules/seo';
import type { HeadersFunction, MetaFunction } from 'react-router';
import customiOS from '~/styles/customiOS.css?url';
import customSafari from '~/styles/customSafari.css?url';
import desktopOnly from '~/styles/desktopOnly.css?url';
import matrixStyle from '@modules/error/component/matrix-rain/matrix-rain.css?url';
// `?url&no-inline` is load-bearing: Vite's `?url` alone still respects
// `build.assetsInlineLimit` (4096 bytes by default), and `fonts.css` is well
// under that. If it inlines as a `data:text/css;base64,...` URL, the relative
// `url('./fonts/...')` references resolve against the document URL instead of
// the original CSS asset path and the woff/woff2 files 404 in production.
// `?no-inline` forces Vite to emit a hashed file URL so the rebased references
// stay valid. See `packages/rocco/src/fonts.css` for the broader rationale.
import roccoFontsCSS from '@rocco/fonts.css?url&no-inline';
import tailwindCSS from './tailwind.css?url';

// Root never updates - It's set per document request.
export const shouldRevalidate: ShouldRevalidateFunction = () => {
    return false;
};

export const meta: MetaFunction<typeof loader> = (props) => {
    const data = props?.data;
    const stage = data?.env?.stage;
    const device = data?.device;
    const links = [];

    if (device?.type === 'desktop') {
        links.push({
            tagName: 'link',
            rel: 'stylesheet',
            href: desktopOnly,
        });
    }
    if (device?.type !== 'desktop' && device?.os.isApple) {
        links.push({
            tagName: 'link',
            rel: 'stylesheet',
            href: customiOS,
        });
    }
    if (device?.browser.isSafari) {
        links.push({
            tagName: 'link',
            rel: 'stylesheet',
            href: customSafari,
        });
    }

    if (data?.env.stage !== 'Production') {
        links.push({
            tagName: 'link',
            rel: 'stylesheet',
            href: matrixStyle,
        });
    }

    // Load self-hosted rocco fonts (ArchiProDisplay, TWKGhost) via a
    // standalone CSS asset so Vite rebases the relative font URLs. See the
    // header in `packages/rocco/src/fonts.css` for the full rationale.
    links.push({
        tagName: 'link',
        rel: 'stylesheet',
        href: roccoFontsCSS,
    });

    links.push({
        tagName: 'link',
        rel: 'stylesheet',
        href: tailwindCSS,
    });

    // Neue Haas Grotesk is not preloaded: it is served by the Adobe Fonts kit
    // (see RootPage `links()`) from rotating hashed p.typekit.net URLs that have
    // no stable href to preload. The kit request starting early, plus the
    // metric-matched Helvetica/Arial fallback in rocco's tailwind config, is what
    // keeps the swap cheap.

    const themeMetaTags = getThemeMetaTags(props);
    return [
        ...(themeMetaTags ? themeMetaTags : []),
        ...(stage !== 'Production'
            ? [{ name: 'robots', content: 'noindex' }]
            : []),
        ...links,
    ];
};

export const headers: HeadersFunction = () => {
    return {
        // Set cache-control header to prevent from caching document requests
        'Cache-Control': 'no-store',
    };
};

export type RootData = SerializeFrom<typeof loader>;

/**
 * error free loader. Proxy the context to the client.
 */
export const loader = async ({ context }: LoaderFunctionArgs) => {
    const {
        env,
        config,
        user,
        localisation,
        device,
        requestId,
        appDisplayMode,
        globalPrivacyControl,
        saleSharingOptOut,
        saleSharingOptOutPending,
    } = context;

    const growthbookContext = await context.getABTests();

    return {
        env,
        config,
        user,
        localisation,
        device,
        requestId,
        appDisplayMode,
        globalPrivacyControl,
        saleSharingOptOut,
        saleSharingOptOutPending,
        growthbookContext,
    };
};

export { links, ErrorBoundary } from '@modules/root/page/RootPage';
export default RootPage;
